/**
 * Matching tolerante de planes.
 *
 * GECROS (externo, en AWS) entrega el plan como un label plano que no siempre
 * coincide con nuestra denominación: puede venir como "PLAN 100", "PLAN100" o
 * solamente "100", mientras que nuestra DB guarda "Plan 100". Este módulo
 * normaliza ambos lados para poder resolver el plan sin importar el formato.
 */

export interface PlanLike {
    denominacion?: string | null;
}

/**
 * Normaliza un label de plan para poder compararlo.
 *
 * Saca el token "plan" (suelto o pegado al número), los diacríticos, el case
 * y los espacios sobrantes.
 *
 *   'PLAN 100'          -> '100'
 *   'PLAN100'           -> '100'
 *   '100'               -> '100'
 *   'Plan Integral 205' -> 'integral 205'
 */
export function normalizarPlan(valor: string | null | undefined): string {
    if (valor === null || valor === undefined) {
        return '';
    }

    return valor
        .toString()
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        // "plan100" / "plan 100" -> "100"
        .replace(/plan(?=\s*\d)/g, ' ')
        // "plan integral 205" -> "integral 205"
        .replace(/\bplan\b/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
}

/** Clave solo alfanumérica: 'Plan 100' -> '100', 'Plan A-2' -> 'a2'. */
export function clavePlan(valor: string | null | undefined): string {
    return normalizarPlan(valor).replace(/[^a-z0-9]/g, '');
}

/** Clave solo numérica: 'Plan Integral 205' -> '205'. */
export function claveNumericaPlan(valor: string | null | undefined): string {
    return normalizarPlan(valor).replace(/\D/g, '');
}

/**
 * Resuelve un label de plan de GECROS contra nuestros planes locales.
 *
 * Prueba, en orden:
 *   1. igualdad normalizada  ('100' === '100')
 *   2. clave alfanumérica    ('plan100' === '100')
 *   3. clave numérica        ('205' === 'integral 205')
 *
 * Devuelve el plan encontrado, o undefined si no matchea ninguno.
 */
export function buscarPlanPorGecros(
    planGecros: string | null | undefined,
    planes: PlanLike[]
): PlanLike | undefined {
    if (!planGecros || !planes?.length) {
        return undefined;
    }

    const objetivoNormalizado = normalizarPlan(planGecros);
    const objetivoClave = clavePlan(planGecros);
    const objetivoNumerico = claveNumericaPlan(planGecros);

    if (!objetivoNormalizado && !objetivoClave && !objetivoNumerico) {
        return undefined;
    }

    const niveles: Array<(plan: PlanLike) => boolean> = [
        (plan) => !!objetivoNormalizado && normalizarPlan(plan.denominacion) === objetivoNormalizado,
        (plan) => !!objetivoClave && clavePlan(plan.denominacion) === objetivoClave,
        (plan) => !!objetivoNumerico && claveNumericaPlan(plan.denominacion) === objetivoNumerico,
    ];

    for (const coincide of niveles) {
        const coincidencias = planes.filter(
            (plan) => !!plan?.denominacion && coincide(plan)
        );

        if (coincidencias.length === 1) {
            return coincidencias[0];
        }

        if (coincidencias.length > 1) {
            console.warn(
                `[plan.util] Coincidencia ambigua para "${planGecros}":`,
                coincidencias.map((plan) => plan.denominacion)
            );
            return coincidencias[0];
        }
    }

    return undefined;
}
